import { Component } from '@theme/component';

export class AnnouncementBar extends Component {
  #current = 0;
  #interval = undefined;
  #hovered = false;
  #listeners;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.#hovered = false;
    this.addEventListener('pointerenter', this.#handlePointerEnter, options);
    this.addEventListener('pointerleave', this.#handlePointerLeave, options);
    this.addEventListener('pointercancel', this.#handlePointerLeave, options);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, options);
    window.addEventListener('pageshow', this.#handlePageShow, options);
    window.addEventListener('pagehide', this.#handlePageHide, options);
    this.resume();
  }

  disconnectedCallback() {
    this.suspend();
    this.#listeners?.abort();
    super.disconnectedCallback();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  play(interval = this.autoplayInterval) {
    this.suspend();
    if (!this.autoplay) return;
    this.paused = false;
    this.#start(interval);
  }

  #start(interval = this.autoplayInterval) {
    this.suspend();
    if (!this.isConnected || !this.autoplay || this.paused ||
        document.hidden || this.#hovered ||
        !Number.isFinite(interval) || interval <= 0) return;
    this.#interval = window.setInterval(() => {
      if (!document.hidden && !this.#hovered && !this.paused) this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    this.toggleAttribute('paused', value);
  }

  suspend() {
    window.clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    this.#start();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const value = Number.parseInt(this.getAttribute('autoplay') || '', 10);
    return Number.isFinite(value) && value > 0 ? value * 1000 : undefined;
  }

  get current() {
    return this.#current;
  }

  set current(value) {
    const slides = this.refs.slides ?? [];
    if (!slides.length) {
      this.#current = 0;
      return;
    }
    this.#current = ((value % slides.length) + slides.length) % slides.length;
    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', String(index !== this.#current));
    });
  }

  #handlePointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hovered = true;
    this.suspend();
  };

  #handlePointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hovered = false;
    this.resume();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#hovered = false;
      this.suspend();
    } else {
      this.resume();
    }
  };

  #handlePageShow = () => {
    this.#hovered = false;
    this.resume();
  };

  #handlePageHide = () => this.suspend();
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}